import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { HERO_SLIDE_EVENT } from '../../../components/feature/Navbar';

// Sequência: Engenharia de Segurança → Avaliações e Perícias → Segurança do Trabalho → Prevenção de Incêndio
const slides = [
  {
    // posição 0 — Engenharia de Segurança
    image: 'https://public.readdy.ai/ai/img_res/edited_93a5023baeed58163619989d78a97d4b_7df44285.jpg',
    imageMobile: 'https://static.readdy.ai/image/c4e06bad978995a8efa8373b87e2590a/a92f4365513c7966b37e89b2c00e140c.png',
    label: 'Engenharia de Segurança',
    icon: 'ri-building-line',
    bgPosition: '85% center',
    bgSize: '105%',
    mirror: true,
    overlayCenter: 0.10,
    isFirst: true,
  },
  {
    // posição 1 — Avaliações e Perícias
    image: 'https://public.readdy.ai/ai/img_res/8a76409ebdda8ba92d8a6e86cc09efed.jpg',
    label: 'Avaliações e Perícias',
    icon: 'ri-file-search-line',
    bgPosition: 'center center',
    bgSize: 'cover',
    mirror: false,
    overlayCenter: 0.10,
    isFirst: false,
  },
  {
    // posição 2 — Segurança do Trabalho
    image: 'https://readdy.ai/api/search-image?query=dark%20moody%20industrial%20workplace%20safety%20equipment%20hard%20hat%20gloves%20PPE%20tools%20on%20dark%20concrete%20floor%20dramatic%20low%20key%20lighting%20deep%20shadows%20black%20background%20cinematic%20atmosphere%20occupational%20health%20safety%20engineering%20high%20contrast%20minimal%20light%20technical%20precision&width=1920&height=1080&seq=hero-safety-dark-102&orientation=landscape',
    label: 'Segurança do Trabalho',
    icon: 'ri-shield-check-line',
    bgPosition: 'center center',
    bgSize: 'cover',
    mirror: false,
    overlayCenter: 0.0,
    isFirst: false,
  },
  {
    // posição 3 — Prevenção de Incêndio
    image: 'https://public.readdy.ai/ai/img_res/edited_57311ba79266bd2d20b6d78a4c9f9c61_2b7aabb3.jpg',
    label: 'Prevenção de Incêndio',
    icon: 'ri-fire-line',
    bgPosition: 'center center',
    bgSize: 'cover',
    mirror: false,
    overlayCenter: -0.45,
    isFirst: false,
  },
];

// --- Partículas ---
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  alpha: number;
  alphaDir: number;
}

const ParticlesCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animFrameRef = useRef<number>(0);

  const initParticles = useCallback((w: number, h: number) => {
    const count = Math.floor((w * h) / 14000);
    particlesRef.current = Array.from({ length: count }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      radius: Math.random() * 1.5 + 0.4,
      alpha: Math.random() * 0.5 + 0.1,
      alphaDir: Math.random() > 0.5 ? 1 : -1,
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      initParticles(canvas.width, canvas.height);
    };

    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const particles = particlesRef.current;

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(212, 175, 55, ${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.alpha += p.alphaDir * 0.003;
        if (p.alpha >= 0.6 || p.alpha <= 0.05) p.alphaDir *= -1;

        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 175, 55, ${p.alpha})`;
        ctx.fill();
      });

      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [initParticles]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full z-[2] pointer-events-none"
    />
  );
};

const HeroSection = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const [current, setCurrent] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 8000);
  }, []);

  const goToSlide = useCallback((index: number) => {
    setCurrent(index);
    startTimer();
  }, [startTimer]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(HERO_SLIDE_EVENT, { detail: { slideIndex: current } })
    );
  }, [current]);

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startTimer]);

  const slide = slides[current];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Slideshow de fundo */}
      <AnimatePresence>
        <motion.div
          key={current}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: 'easeInOut' }}
        >
          {/* Imagem de fundo */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                isMobile && slide.isFirst && slide.imageMobile
                  ? `url(${slide.imageMobile})`
                  : `url(${slide.image})`,
              backgroundPosition:
                isMobile && slide.isFirst ? 'center top' : slide.bgPosition,
              backgroundSize:
                isMobile && slide.isFirst ? 'cover' : slide.bgSize,
              backgroundRepeat: 'no-repeat',
              transform:
                isMobile && slide.isFirst
                  ? 'none'
                  : slide.mirror
                  ? 'scaleX(-1)'
                  : 'none',
            }}
          />

          {/* Overlay gradiente */}
          <div
            className="absolute inset-0"
            style={{
              background: (() => {
                if (isMobile && !slide.isFirst && slide.overlayCenter >= 0) {
                  return `radial-gradient(ellipse at center, rgba(0,0,0,${slide.overlayCenter * 0.5}) 0%, rgba(0,0,0,0.22) 50%, rgba(0,0,0,0.55) 100%)`;
                }
                return `radial-gradient(ellipse at center, rgba(0,0,0,${slide.overlayCenter}) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.88) 100%)`;
              })(),
            }}
          />

          {/* Bordas escuras */}
          <div
            className="absolute inset-0"
            style={{
              boxShadow: slide.isFirst
                ? 'inset 0 0 80px 30px rgba(0,0,0,0.55)'
                : isMobile
                ? 'inset 0 0 80px 30px rgba(0,0,0,0.45)'
                : 'inset 0 0 160px 80px rgba(0,0,0,0.90)',
            }}
          />

          {/* Gradiente topo */}
          <div
            className="absolute inset-x-0 top-0"
            style={{
              height: slide.isFirst ? '18%' : '30%',
              background: slide.isFirst
                ? 'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 100%)'
                : isMobile
                ? 'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 100%)'
                : 'linear-gradient(to bottom, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)',
            }}
          />

          {/* Gradiente base */}
          <div
            className="absolute inset-x-0 bottom-0"
            style={{
              height: slide.isFirst ? '18%' : '30%',
              background: slide.isFirst
                ? 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 100%)'
                : isMobile
                ? 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 100%)'
                : 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)',
            }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Partículas */}
      <ParticlesCanvas />

      {/* Conteúdo */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-28 pb-32 sm:py-32 relative z-10 w-full">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-[#D4AF37]/20 backdrop-blur-sm border border-[#D4AF37]/30 text-[#D4AF37] px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-[9px] sm:text-xs font-bold tracking-wider mb-3 sm:mb-6"
            >
              <i className="ri-star-fill text-[9px] sm:text-sm"></i>
              REFERÊNCIA REGIONAL EM ENGENHARIA
            </motion.div>

            <h1 className="font-display font-extrabold text-2xl sm:text-5xl lg:text-7xl text-white leading-snug mb-3 sm:mb-6">
              <span className="sm:hidden">Regularize sua Empresa com Segurança e Responsabilidade Técnica</span>
              <span className="hidden sm:inline">Engenharia de Excelência para a Segurança da sua Empresa</span>
            </h1>

            <h2 className="text-base sm:text-2xl lg:text-3xl text-[#D4AF37] font-bold leading-snug mb-2 sm:mb-6">
              Regiões Metropolitanas de Sorocaba e Campinas
            </h2>

            <p className="text-xs sm:text-lg lg:text-xl text-neutral-300 leading-relaxed mb-6 sm:mb-10 max-w-3xl">
              Soluções técnicas especializadas em Prevenção de Incêndio, Segurança do Trabalho e Avaliações Periciais. Projetos, laudos e assessoria completa com responsabilidade técnica e conformidade normativa.
            </p>

            <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-4">
              <a
                href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Vim pelo site (página inicial) e gostaria de solicitar um orçamento.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#D4AF37] text-[#0A1628] px-6 sm:px-8 py-3 sm:py-4 rounded-md text-xs sm:text-base font-bold hover:bg-[#C4A137] transition-all flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <span className="sm:hidden">ANÁLISE TÉCNICA GRATUITA</span>
                <span className="hidden sm:inline">SOLICITAR ORÇAMENTO</span>
                <i className="ri-arrow-right-line text-base sm:text-xl"></i>
              </a>
              <Link
                to="/servicos"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-md text-xs sm:text-base font-bold hover:bg-white hover:text-[#0A1628] transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                NOSSOS SERVIÇOS
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Seta de scroll */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-white/60 cursor-pointer"
        >
          <i className="ri-arrow-down-line text-3xl"></i>
        </motion.div>
      </div>

      {/* Dots de navegação */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-10 flex items-center gap-3">
        {slides.map((s, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            title={s.label}
            className="group flex items-center justify-center cursor-pointer"
          >
            <motion.div
              animate={{
                width: current === index ? 12 : 8,
                height: current === index ? 12 : 8,
                opacity: current === index ? 1 : 0.45,
              }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              className="rounded-full bg-[#D4AF37]"
            />
          </button>
        ))}
      </div>
    </section>
  );
};

export default HeroSection;
